import {Button} from '@wandb/weave/components/Button';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {getModelsByIds} from '../../inference/modelInfo';
import {getPlaygroundModelString} from '../../inference/util';
import {normalizeChatTraceCall} from '../ChatView/hooks';
import {useShowMarkdownInChatView} from '../ChatView/useShowMarkdownInChatView';
import {SimplePageLayoutWithHeader} from '../common/SimplePageLayout';
import {EvaluationPlaygroundPage} from '../EvaluationPlaygroundPage/EvaluationPlaygroundPage';
import {useWFHooks} from '../wfReactInterface/context';
import {
  useBaseObjectInstances,
  useLeafObjectInstances,
} from '../wfReactInterface/objectClassQuery';
import {projectIdFromParts} from '../wfReactInterface/tsDataModelHooks';
import {LLM_MAX_TOKENS, LLMMaxTokensKey} from './llmMaxTokens';
import {PlaygroundChat} from './PlaygroundChat/PlaygroundChat';
import {PlaygroundSettings} from './PlaygroundSettings/PlaygroundSettings';
import {useConfiguredProviders} from './useConfiguredProviders';
import {usePlaygroundState} from './usePlaygroundState';

export type PlaygroundPageProps = {
  entity: string;
  project: string;
  callId: string;
  // IDs of models from the inference catalog
  modelIds: string[];
};

type PlaygroundPageInnerProps = PlaygroundPageProps &
  ReturnType<typeof usePlaygroundState> & {
    settingsTab: number | null;
    setSettingsTab: Dispatch<SetStateAction<number | null>>;
  };

const isLLMMaxTokensKey = (key: string): key is LLMMaxTokensKey => {
  return key in LLM_MAX_TOKENS;
};

export const PlaygroundPage = (props: PlaygroundPageProps) => {
  const [tabValue, setTabValue] = useState('Prompt');
  const [settingsTab, setSettingsTab] = useState<number | null>(null);
  const onTabSelectedCallback = useCallback((tab: string) => {
    setTabValue(tab);
  }, []);

  // We expect the passed in models to all be in LLM_MAX_TOKENS,
  // but ignore them if they are not.
  const initialModelStrings = getModelsByIds(
    props.modelIds.filter(isLLMMaxTokensKey)
  ).map(getPlaygroundModelString);

  const {
    setPlaygroundStates,
    playgroundStates,
    setPlaygroundStateField,
    setPlaygroundStateFromTraceCall,
  } = usePlaygroundState(initialModelStrings);

  return (
    <SimplePageLayoutWithHeader
      title="Playground"
      hideTabsIfSingle
      headerContent={null}
      tabs={[
        {
          label: 'Prompt',
          content: (
            <Tailwind style={{width: '100%', height: '100%'}}>
              <PromptPlaygroundPage
                setPlaygroundStates={setPlaygroundStates}
                playgroundStates={playgroundStates}
                setPlaygroundStateField={setPlaygroundStateField}
                setPlaygroundStateFromTraceCall={
                  setPlaygroundStateFromTraceCall
                }
                settingsTab={settingsTab}
                setSettingsTab={setSettingsTab}
                {...props}
              />
            </Tailwind>
          ),
        },
        {
          label: 'Evaluate',
          content: (
            <EvaluationPlaygroundPage
              entity={props.entity}
              project={props.project}
            />
          ),
        },
      ]}
      onTabSelectedCallback={onTabSelectedCallback}
      headerExtra={
        tabValue === 'Prompt' ? (
          <Button
            variant="ghost"
            size="medium"
            icon="add-new"
            onClick={() => {
              setPlaygroundStates([
                ...playgroundStates,
                JSON.parse(JSON.stringify(playgroundStates[settingsTab ?? 0])),
              ]);
            }}>
            Add model
          </Button>
        ) : null
      }
    />
  );
};

const PromptPlaygroundPage = ({
  playgroundStates,
  setPlaygroundStateField,
  setPlaygroundStateFromTraceCall,
  settingsTab,
  setSettingsTab,
  setPlaygroundStates,
  entity,
  project,
  callId,
}: PlaygroundPageInnerProps) => {
  const {useCall, useCalls} = useWFHooks();
  const {showMarkdownInChatView, setShowMarkdownInChatView} =
    useShowMarkdownInChatView();
  const projectId = useMemo(
    () => projectIdFromParts({entity, project}),
    [entity, project]
  );
  const callKey = useMemo(() => {
    return callId
      ? {
          entity,
          project,
          callId,
        }
      : null;
  }, [entity, project, callId]);

  const call = useCall({key: callKey});
  const callWithCosts = useCall({key: callKey, includeCosts: true});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {result: calls} = useCalls({
    entity,
    project,
    filter: {
      callIds: playgroundStates.map(state => state.traceCall.id || ''),
    },
    includeCosts: true,
  });

  const {
    result: configuredProviders,
    loading: configuredProvidersLoading,
    refetch: refetchConfiguredProviders,
  } = useConfiguredProviders(entity);

  const {
    result: customProvidersResult,
    loading: customProvidersLoading,
    refetch: refetchCustomProviders,
  } = useBaseObjectInstances('Provider', {
    project_id: projectId,
    filter: {
      latest_only: true,
    },
  });

  const {
    result: customProviderModelsResult,
    loading: customProviderModelsLoading,
    refetch: refetchCustomProviderModels,
  } = useBaseObjectInstances('ProviderModel', {
    project_id: projectId,
    filter: {
      latest_only: true,
    },
  });

  const {
    result: savedModelsResult,
    loading: savedModelsLoading,
    refetch: refetchSavedModels,
  } = useLeafObjectInstances('LLMStructuredCompletionModel', {
    project_id: projectId,
  });

  const refetchCustomLLMs = useCallback(() => {
    refetchCustomProviders();
    refetchCustomProviderModels();
  }, [refetchCustomProviders, refetchCustomProviderModels]);

  const areCustomProvidersLoading =
    customProvidersLoading || customProviderModelsLoading;

  useEffect(() => {
    if (!call.loading && call.result) {
      if (call.result.traceCall?.inputs) {
        setPlaygroundStateFromTraceCall(call.result.traceCall);

        // Extract prompt ref from call inputs and set it in state
        // The PlaygroundChatTopBar will handle loading the prompt details
        const promptRef = call.result.traceCall.inputs.prompt || '';
        if (promptRef) {
          setPlaygroundStateField(0, 'prompt', (prev: any) => ({
            ...(prev || {}),
            promptRef,
            // Don't set promptName here - let PlaygroundChatTopBar resolve it
            // from promptOptions when it has them loaded
          }));
        } else {
          setPlaygroundStateField(0, 'prompt', (prev: any) => ({
            ...(prev || {}),
            promptRef: '',
            messages: [],
            promptName: '',
            isModified: false,
          }));
        }
      }
    } else if (
      playgroundStates.length === 1 &&
      !playgroundStates[0].traceCall.project_id
    ) {
      setPlaygroundStateField(0, 'traceCall', {
        inputs: {
          messages: [],
        },
        project_id: projectId,
      });
    }
    // Only set the call the first time the page loads, and we get the call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.loading]);

  useEffect(() => {
    if (!callWithCosts.loading && callWithCosts.result) {
      if (callWithCosts.result.traceCall.inputs) {
        setPlaygroundStateFromTraceCall(callWithCosts.result.traceCall);

        // Extract prompt ref from call inputs and set it in state
        // The PlaygroundChatTopBar will handle loading the prompt details
        const promptRef = callWithCosts.result.traceCall.inputs.prompt || '';
        if (promptRef) {
          setPlaygroundStateField(0, 'prompt', (prev: any) => ({
            ...(prev || {}),
            promptRef,
            // Don't set promptName here - let PlaygroundChatTopBar resolve it
            // from promptOptions when it has them loaded
          }));
        } else {
          setPlaygroundStateField(0, 'prompt', (prev: any) => ({
            ...(prev || {}),
            promptRef: '',
            messages: [],
            promptName: '',
            isModified: false,
          }));
        }
      }
    }
    // Only set the call the first time the page loads, and we get the call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callWithCosts.loading]);

  useEffect(() => {
    setPlaygroundStates(prev => {
      const newStates = [...prev];
      for (const [idx, state] of newStates.entries()) {
        for (const c of calls || []) {
          if (state.traceCall.id === c.callId) {
            // Preserve template_vars before normalization
            const templateVars = (c.traceCall?.inputs as any)?.template_vars;

            // Update the trace call with the latest data
            const normalizedTraceCall = normalizeChatTraceCall(
              c.traceCall || {}
            );

            // Restore template_vars if they existed
            if (templateVars && normalizedTraceCall.inputs) {
              (normalizedTraceCall.inputs as any).template_vars = templateVars;
            }

            // Check if prompt messages were prepended to this request
            // (happens when prompt is modified or has no promptRef)
            if (
              state.prompt?.messages?.length > 0 &&
              (!state.prompt?.promptRef || state.prompt?.isModified) &&
              normalizedTraceCall.inputs
            ) {
              const promptMessageCount = state.prompt.messages.length;
              // Trim the prepended prompt messages from the refetched trace
              if (
                normalizedTraceCall.inputs.messages?.length > promptMessageCount
              ) {
                normalizedTraceCall.inputs.messages =
                  normalizedTraceCall.inputs.messages.slice(promptMessageCount);
              }
            }

            newStates[idx] = {
              ...state,
              traceCall: normalizedTraceCall,
            };

            break;
          }
        }
      }
      return newStates;
    });
  }, [calls, setPlaygroundStates]);

  return (
    <div className="flex h-full w-full">
      {call.loading ||
      configuredProvidersLoading ||
      areCustomProvidersLoading ? (
        <div className="flex h-full w-full">
          <WaveLoader size="huge" />
        </div>
      ) : (
        <PlaygroundChat
          playgroundStates={playgroundStates}
          setPlaygroundStates={setPlaygroundStates}
          setPlaygroundStateField={setPlaygroundStateField}
          entity={entity}
          project={project}
          setSettingsTab={setSettingsTab}
          settingsTab={settingsTab}
          isOpenInPlayground={!!call.result}
          configuredProvidersLoading={configuredProvidersLoading}
          refetchConfiguredProviders={refetchConfiguredProviders}
          areCustomProvidersLoading={areCustomProvidersLoading}
          refetchCustomLLMs={refetchCustomLLMs}
          customProvidersResult={customProvidersResult || []}
          customProviderModelsResult={customProviderModelsResult || []}
          configuredProviders={configuredProviders}
          savedModelsResult={savedModelsResult || []}
          savedModelsLoading={savedModelsLoading}
          showMarkdownInChatView={showMarkdownInChatView}
          setShowMarkdownInChatView={setShowMarkdownInChatView}
        />
      )}
      {settingsTab !== null && (
        <PlaygroundSettings
          playgroundStates={playgroundStates}
          setPlaygroundStateField={setPlaygroundStateField}
          settingsTab={settingsTab}
          setSettingsTab={setSettingsTab}
          projectId={projectId}
          refetchSavedModels={refetchSavedModels}
          savedModelsResult={savedModelsResult || []}
          showMarkdownInChatView={showMarkdownInChatView}
          setShowMarkdownInChatView={setShowMarkdownInChatView}
        />
      )}
    </div>
  );
};
